import { Button, Form, message, Modal, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import { clipboardText } from '@/common/utils/clipboard';
import { isLanguageEn } from '@/common/utils/language';
import { useMeetingCopyText } from '@/common/utils/meeting-copy-text';
import { TStore } from '@/common/basis/types/tstore';
import { setMeetingShareData } from '@/common/store/popup/slice';

function App() {
  const dispatch = useDispatch();
  const meetingShareData = useSelector((state: TStore.State) => state.popup.meetingShareData);
  const roomId = useMemo(() => meetingShareData?.roomId ?? '', [meetingShareData?.roomId]);
  const roomName = useMemo(() => meetingShareData?.roomName ?? '', [meetingShareData?.roomName]);
  const brightPassword = useMemo(() => meetingShareData?.brightPassword ?? '', [meetingShareData?.brightPassword]);
  const webUrl = useMemo(() => meetingShareData?.webUrl ?? '', [meetingShareData?.webUrl]);
  const liveUrl = useMemo(() => meetingShareData?.liveUrl ?? '', [meetingShareData?.liveUrl]);
  const isOpen = useMemo(() => !!meetingShareData, [meetingShareData]);
  const isMeetingLive = useMemo(() => !!liveUrl, [liveUrl]);
  const [messageApi, contextHolder] = message.useMessage({ top: 60 });
  const { meetingText } = useMeetingCopyText({ roomId, roomName, brightPassword, webUrl, liveUrl });
  const { t } = useTranslation();

  return (
    <>
      <Modal
        footer={null}
        maskClosable={false}
        onCancel={() => dispatch(setMeetingShareData(undefined))}
        open={isOpen}
        title={roomName}
        width={isLanguageEn ? 520 : 480}
        zIndex={99999}
      >
        <div className='pb-2 pt-2'>
          <Form
            colon={false}
            labelAlign='left'
            labelCol={{ flex: isLanguageEn ? '120px' : isMeetingLive ? '94px' : '80px' }}
            layout='horizontal'
          >
            <Form.Item
              className='mb-0 min-h-[30px]'
              label={<span className='text-[16px] text-[rgba(19,20,21)]'>{t('meetingId')}</span>}
            >
              <div className='py-1 text-right'>
                <Typography.Text
                  className='text-[16px] text-[rgba(19,20,21)]'
                  copyable={{ onCopy: () => clipboardText(roomId) }}
                >
                  {roomId}
                </Typography.Text>
              </div>
            </Form.Item>
            <Form.Item
              className='mb-0 min-h-[30px]'
              label={<span className='text-[16px] text-[rgba(19,20,21)]'>{t('joinPassword')}</span>}
            >
              <div className='py-1 text-right'>
                <Typography.Text
                  className='text-[16px] text-[rgba(19,20,21)]'
                  copyable={{ onCopy: () => clipboardText(brightPassword) }}
                >
                  {brightPassword}
                </Typography.Text>
              </div>
            </Form.Item>
            <Form.Item
              className='mb-0 min-h-[30px]'
              label={
                <span className='text-[16px] text-[rgba(19,20,21)]'>
                  {isMeetingLive ? t('participantLink') : t('meetingLink')}
                </span>
              }
              labelAlign='right'
            >
              <div className='flex justify-end py-1 pt-[6px]' style={{ lineBreak: 'anywhere' }}>
                <div className='mr-1 line-clamp-2 overflow-hidden text-[16px] text-[rgba(19,20,21)]'>{webUrl}</div>
                <Typography.Text className='text-[16px]' copyable={{ onCopy: () => clipboardText(webUrl) }} />
              </div>
            </Form.Item>
            {isMeetingLive ? (
              <Form.Item
                className='mb-0 min-h-[30px]'
                label={<span className='text-[16px] text-[rgba(19,20,21)]'>{t('audienceLink')}</span>}
              >
                <div className='flex justify-end py-1 pt-[6px]' style={{ lineBreak: 'anywhere' }}>
                  <div className='mr-1 line-clamp-2 overflow-hidden text-[16px] text-[rgba(19,20,21)]'>{liveUrl}</div>
                  <Typography.Text className='text-[16px]' copyable={{ onCopy: () => clipboardText(liveUrl) }} />
                </div>
              </Form.Item>
            ) : (
              <></>
            )}
            <Form.Item className='mb-0 mt-6 flex justify-end'>
              <Button
                onClick={async () => {
                  const isCopySuccess = await clipboardText(meetingText);

                  if (isCopySuccess) {
                    messageApi.destroy();
                    messageApi.success(t('copySuccess'));
                  } else {
                    messageApi.destroy();
                    messageApi.error(t('coypFail'));
                  }
                  dispatch(setMeetingShareData(undefined)); // 清空分享的数据
                }}
                size='large'
                type='primary'
              >
                <span>{t('copyMeetingInfo')}</span>
              </Button>
            </Form.Item>
          </Form>
        </div>
      </Modal>
      {contextHolder}
    </>
  );
}

export default App;
